import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../components/CartContext';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../contexts/CurrencyContext';

const CheckoutPage = () => {
  const { cart, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { t } = useTranslation();
  const { formatPrice, selectedCurrency, changeCurrency } = useCurrency();
  const [isRedirectingToCPay, setIsRedirectingToCPay] = useState(false);
  const [freeShippingThreshold, setFreeShippingThreshold] = useState(0);

  // New state variables for shipping and coupons
  const [shippingCost, setShippingCost] = useState(0);
  const [shippingMethods, setShippingMethods] = useState([]);
  const [selectedShippingMethod, setSelectedShippingMethod] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    // Fetch shipping countries from WooCommerce
    const fetchShippingCountries = async () => {
      try {
        const response = await fetch(window.wcSettings.ajax_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            action: 'get_shipping_countries',
            nonce: window.wcSettings.custom_checkout_nonce,
          }),
        });

        if (!response.ok) throw new Error('Failed to fetch shipping countries');

        const result = await response.json();
        if (result.success) {
          setCountries(result.data);
        }
      } catch (error) {
        console.error('Error fetching shipping countries:', error);
      }
    };

    fetchShippingCountries();
  }, []);

  // Helper function to get country code from currency
  const getCountryFromCurrency = (currency) => {
    // Map currency back to a default country
    if (currency === 'MKD') return 'MK';
    if (currency === 'EUR') return 'AL'; // Default to Albania for EUR
    return 'MK'; // Default fallback
  };

  const [formData, setFormData] = useState(() => {
    // Initialize with country from localStorage or default based on currency
    const savedCountry = localStorage.getItem('selectedCountry');
    const initialCountry = savedCountry || (selectedCurrency === 'MKD' ? 'MK' : 'AL');
    return {
      email: '',
      phone: '',
      firstName: '',
      lastName: '',
      company: '',
      address: '',
      city: '',
      postcode: '',
      country: initialCountry,
      paymentMethod: 'cpay_gateway',
      orderNotes: ''
    };
  });

  // Initialize country based on selected currency on mount
  useEffect(() => {
    const initialCountry = getCountryFromCurrency(selectedCurrency);
    if (formData.country !== initialCountry) {
      setFormData(prev => ({
        ...prev,
        country: initialCountry
      }));
    }
  }, []); // Run only once on mount

  // Redirect if cart is empty
  useEffect(() => {
    if (cart.items.length === 0) {
      navigate('/');
    }
  }, [cart.items.length, navigate]);

  // Fetch shipping methods
  useEffect(() => {
    const fetchShippingMethods = async () => {
      if (!formData.country) return;

      try {
        const response = await fetch(window.wcSettings.ajax_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            action: 'get_shipping_methods',
            nonce: window.wcSettings.custom_checkout_nonce,
            country: formData.country,
          }),
        });

        const result = await response.json();
        if (result.success) {
          const methods = result.data.methods;
          const threshold = result.data.free_shipping_threshold || 0;

          // Filter out free shipping methods that don't meet the threshold
          const availableMethods = methods.filter(method => {
            if (method.id.startsWith('free_shipping:')) {
              // Only include free shipping if cart meets threshold
              return threshold > 0 && cart.subtotal >= threshold;
            }
            return true; // Include all other shipping methods
          });

          setShippingMethods(availableMethods);
          setFreeShippingThreshold(threshold);

          // Set default shipping method - prioritize free shipping
          if (availableMethods.length > 0) {
            const freeShippingMethod = availableMethods.find(method => method.id.startsWith('free_shipping:'));
            if (freeShippingMethod) {
              setSelectedShippingMethod(freeShippingMethod.id);
            } else {
              setSelectedShippingMethod(availableMethods[0].id);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch shipping methods:', error);
      }
    };

    fetchShippingMethods();
  }, [formData.country, cart.subtotal]);
  useEffect(() => {
    if (formData.country) {
      const newCurrency = getCurrencyByCountry(formData.country);
      if (newCurrency !== selectedCurrency) {
        changeCurrency(newCurrency);
      }
    }
  }, [formData.country]); // Only depend on country change
  // Calculate shipping cost
  useEffect(() => {
    const selectedMethod = shippingMethods.find(method => method.id === selectedShippingMethod);
    setShippingCost(selectedMethod ? parseFloat(selectedMethod.cost) : 0);
  }, [selectedShippingMethod, shippingMethods]);

  // Apply coupon function
  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError(t('checkout.errors.enterCouponCode'));
      return;
    }

    setCouponLoading(true);
    setCouponError('');

    try {
      const response = await fetch(window.wcSettings.ajax_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          action: 'apply_coupon',
          nonce: window.wcSettings.custom_checkout_nonce,
          coupon_code: couponCode.trim(),
          cart_total: cart.subtotal,
          cart_items: JSON.stringify(cart.items),
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setAppliedCoupon(result.data);
        setCouponCode('');
        setCouponError('');
        console.log('Coupon applied successfully:', result.data);
      } else {
        setCouponError(result.data || t('checkout.errors.invalidCoupon'));
      }
    } catch (error) {
      console.error('Coupon application error:', error);
      setCouponError(t('checkout.errors.couponFailed'));
    } finally {
      setCouponLoading(false);
    }
  };

  // Remove coupon function
  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponError('');
  };

  // Calculate final total
  const calculateFinalTotal = () => {
    let total = cart.subtotal + shippingCost;

    if (appliedCoupon) {
      if (appliedCoupon.type === 'percent') {
        total = total - (total * appliedCoupon.amount / 100);
      } else {
        total = total - appliedCoupon.amount;
      }
    }

    return Math.max(0, total);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // If country changes and it's not Macedonia, switch from COD to credit card
      if (field === 'country' && value !== 'MK' && prev.paymentMethod === 'cod') {
        newData.paymentMethod = 'cpay_gateway';
      }
      
      return newData;
    });
  };

  const getCurrencyByCountry = (countryCode) => {
    const currencyMap = {
      'MK': 'MKD',  // North Macedonia - keep MKD
      'AL': 'EUR',  // Albania - use EUR
      'XK': 'EUR',  // Kosovo - use EUR
      'RS': 'EUR',  // Serbia - use EUR
    };
    return currencyMap[countryCode] || 'MKD'; // Default to MKD
  };

  const validateForm = () => {
    const requiredFields = [
      { field: 'email', key: 'emailRequired' },
      { field: 'phone', key: 'phoneRequired' },
      { field: 'firstName', key: 'firstNameRequired' },
      { field: 'lastName', key: 'lastNameRequired' },
      { field: 'address', key: 'addressRequired' },
      { field: 'city', key: 'cityRequired' },
      { field: 'postcode', key: 'postcodeRequired' },
      { field: 'country', key: 'countryRequired' }
    ];

    for (const { field, key } of requiredFields) {
      if (!formData[field].trim()) {
        setError(t(`checkout.errors.${key}`));
        return false;
      }
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(t('checkout.errors.invalidEmail'));
      return false;
    }

    if (!countries.some(c => c.code === formData.country)) {
      setError(t('checkout.errors.shippingNotAvailable'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const billingAddress = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        company: formData.company,
        address_1: formData.address,
        city: formData.city,
        postcode: formData.postcode,
        country: formData.country,
        email: formData.email,
        phone: formData.phone
      };

      const response = await fetch(window.wcSettings.ajax_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          action: 'custom_checkout_process',
          nonce: window.wcSettings.custom_checkout_nonce,
          cart_items: JSON.stringify(cart.items),
          billing: JSON.stringify(billingAddress),
          shipping: JSON.stringify(billingAddress),
          payment_method: formData.paymentMethod,
          order_comments: formData.orderNotes || '',
          shipping_method: selectedShippingMethod,
          shipping_cost: shippingCost,
          coupon_data: appliedCoupon ? JSON.stringify(appliedCoupon) : '',
        }),
      });

      const result = await response.json();

      if (result.success) {
        if (result.data.payment_method === 'cpay' && result.data.cpay_form_url) {
          window.location.href = result.data.cpay_form_url;
        } else if (result.data.redirect) {
          window.location.href = result.data.redirect;
        }
      } else {
        setError(result.data || t('checkout.errors.checkoutFailed'));
      }
    } catch (err) {
      setError(t('checkout.errors.checkoutFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (cart.items.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white px-2">
      <div className="max-w-6xl mx-auto mb-[64px] md:mb-[43px]">
        <div className="text-start mb-[29px] lg:max-w-[708px] lg:mx-auto">
          <h1 className="text-26 font-light mb-[12px]">{t('checkout.title')}</h1>
          <h2 className="text-18 font-normal mb-2">{t('checkout.contactInfo')}</h2>
        </div>

        <div className="grid lg:grid-cols-[350px_350px] lg:gap-2 gap-2 lg:justify-center">
          {/* Left Column - Form */}
          <div className="space-y-8 -translate-y-[21px]">
            <form id="checkout-form" onSubmit={handleSubmit} className="space-y-8 ">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
                  {error}
                </div>
              )}

              {/* Contact Information */}
              <div>
                <div className="space-y-2">
                  <div>
                    <label className="block text-14 font-light text-black mb-[4px]">{t('checkout.email')}</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="w-full h-9 border border-black px-3 text-sm focus:outline-none focus:border-black"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-14 font-light text-black mb-[4px]">{t('checkout.phone')}</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="w-full h-9 border border-black px-3 text-sm focus:outline-none focus:border-black"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div>
                <h2 className="text-18 font-normal mb-2">{t('checkout.shippingAddress')}</h2>
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-14 font-light text-black mb-[4px]">{t('checkout.firstName')}</label>
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        className="w-full h-9 border border-black px-3 text-sm focus:outline-none focus:border-black"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-14 font-light text-black mb-[4px]">{t('checkout.lastName')}</label>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        className="w-full h-9 border border-black px-3 text-sm focus:outline-none focus:border-black"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-14 font-light text-black mb-[4px]">{t('checkout.company')}</label>
                    <input
                      type="text"
                      value={formData.company}
                      onChange={(e) => handleInputChange('company', e.target.value)}
                      className="w-full h-9 border border-black px-3 text-sm focus:outline-none focus:border-black"
                    />
                  </div>
                  <div>
                    <label className="block text-14 font-light text-black mb-[4px]">{t('checkout.address')}</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      className="w-full h-9 border border-black px-3 text-sm focus:outline-none focus:border-black"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-14 font-light text-black mb-[4px]">{t('checkout.city')}</label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        className="w-full h-9 border border-black px-3 text-sm focus:outline-none focus:border-black"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-14 font-light text-black mb-[4px]">{t('checkout.postcode')}</label>
                      <input
                        type="text"
                        value={formData.postcode}
                        onChange={(e) => handleInputChange('postcode', e.target.value)}
                        className="w-full h-9 border border-black px-3 text-sm focus:outline-none focus:border-black"
                        required
                      />
                    </div>
                  </div>
                  <div className="relative">
                    <label className="block text-14 font-light text-black mb-[4px]">
                      {t('checkout.country')} {selectedCurrency && `(${selectedCurrency})`}
                    </label>
                    <div className="relative">
                      <select
                        value={formData.country}
                        onChange={(e) => {
                          const newCountry = e.target.value;
                          handleInputChange('country', newCountry);
                          console.log('Country changed to:', newCountry);
                        }}
                        onFocus={() => setIsDropdownOpen(true)}
                        onBlur={() => setIsDropdownOpen(false)}
                        className="w-full h-9 border border-black px-3 pr-8 text-sm focus:outline-none focus:border-black bg-white appearance-none cursor-pointer"
                        required
                      >
                        {countries.map(country => (
                          <option key={country.code} value={country.code}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        {isDropdownOpen ? (
                          <svg width="16" height="8" viewBox="0 0 16 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M14.8182 7.54545L7.90909 1L1 7.54545" stroke="black" strokeMiterlimit="10" />
                          </svg>
                        ) : (
                          <svg width="16" height="8" viewBox="0 0 16 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1.09082 0.727295L7.99991 7.27275L14.909 0.727295" stroke="black" strokeMiterlimit="10" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Shipping Method */}
              {shippingMethods.length > 0 && (
                <div>
                  <h2 className="text-18 font-normal mb-2">{t('checkout.shippingMethod')}</h2>
                  <div className="space-y-2">
                    {shippingMethods.map((method) => {
                      const isFreeShipping = method.id.startsWith('free_shipping:');
                      const hasFreeShipping = shippingMethods.some(m => m.id.startsWith('free_shipping:'));
                      const isDisabled = hasFreeShipping && !isFreeShipping;

                      return (
                        <div
                          key={method.id}
                          className={`border cursor-pointer h-[37px] flex items-center justify-between px-2 ${selectedShippingMethod === method.id ? 'border-black' : 'border-[#BDBDBD]'
                            } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                          onClick={() => !isDisabled && setSelectedShippingMethod(method.id)}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="radio"
                              id={`shipping_${method.id}`}
                              name="shipping_method"
                              value={method.id}
                              checked={selectedShippingMethod === method.id}
                              onChange={(e) => !isDisabled && setSelectedShippingMethod(e.target.value)}
                              className="w-4 h-4 text-black focus:ring-black border-black absolute opacity-0"
                              disabled={isDisabled}
                            />
                            <div className="flex flex-col py-[10px]">
                              <label htmlFor={`shipping_${method.id}`} className={`text-14 cursor-pointer ${selectedShippingMethod === method.id ? 'font-normal' : 'font-light'}`}>
                                {method.title}
                              </label>
                            </div>
                          </div>
                          <span className={`text-14 ${selectedShippingMethod === method.id ? 'font-normal' : 'font-light'}`}>
                            {isFreeShipping ? t('checkout.free') : formatPrice(parseFloat(method.cost))}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Payment Method */}
              <div>
                <h2 className="text-18 font-normal mb-2">{t('checkout.paymentMethod')}</h2>
                <div className="space-y-2">
                  <div className={`border cursor-pointer h-[65px] flex items-center px-2 ${formData.paymentMethod === 'cpay_gateway' ? 'border-black' : 'border-[#BDBDBD]'}`} onClick={() => handleInputChange('paymentMethod', 'cpay_gateway')}>
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        id="cpay_gateway"
                        name="payment"
                        value="cpay_gateway"
                        checked={formData.paymentMethod === 'cpay_gateway'}
                        onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                        className="w-4 h-4 text-black focus:ring-black border-black absolute opacity-0"
                      />
                      <div className="flex flex-col py-[10px]">
                        <label htmlFor="cpay_gateway" className={`text-14 cursor-pointer ${formData.paymentMethod === 'cpay_gateway' ? 'font-normal' : 'font-light'}`}>
                          {t('checkout.creditCard')}
                        </label>
                        <p className={`text-10 font-light mt-[4px]`}>
                          {t('checkout.creditCardDesc')}
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Cash on Delivery - Only available for Macedonia */}
                  {formData.country === 'MK' && (
                    <div className={`border cursor-pointer h-[65px] flex items-center px-2 ${formData.paymentMethod === 'cod' ? 'border-black' : 'border-[#BDBDBD]'}`} onClick={() => handleInputChange('paymentMethod', 'cod')}>
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          id="cod"
                          name="payment"
                          value="cod"
                          checked={formData.paymentMethod === 'cod'}
                          onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                          className="w-4 h-4 text-black focus:ring-black border-black absolute opacity-0"
                        />
                        <div className="flex flex-col py-[10px]">
                          <label htmlFor="cod" className={`text-14 cursor-pointer ${formData.paymentMethod === 'cod' ? 'font-normal' : 'font-light'}`}>
                            {t('checkout.cashOnDelivery')}
                          </label>
                          <p className={`text-10 font-light mt-[4px]`}>
                            {t('checkout.cashOnDeliveryDesc')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>


            </form>
          </div>

          {/* Right Column - Order Summary with border */}
          <div className='translate-y-[11px] md:translate-y-[0px]'>
            {/* Mobile Order Summary Heading - Only visible on mobile */}
            <div className="lg:hidden">
              <h2 className="text-18 font-normal mb-2">{t('checkout.orderSummary')}</h2>
            </div>
            <div className="border border-black p-2 h-fit">
              <div className="space-y-2">
                {cart.items.map((item) => (
                  <div key={`${item.id}-${item.variation_id || 'default'}`} className="flex gap-4">
                    <div className="relative">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-[56px] h-[56px] object-cover bg-[#BDBDBD] border border-black"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-14 font-normal pb-[4px]">{item.name}</h3>
                      <p className="text-14 text-black font-light pb-[6px]">{item.variation?.name || ''}</p>
                      <p className="text-10 font-light text-black">{t('checkout.quantity')} {item.quantity}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-14 font-normal">{formatPrice((item.price * item.quantity))}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Discount Code - Updated design */}
              <div className="border-t border-black pt-2 mt-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    placeholder={t('checkout.discountCode')}
                    className="flex-1 h-9 border border-black px-3 text-sm focus:outline-none focus:border-black placeholder-[#BDBDBD] placeholder:text-14 placeholder:font-normal"
                  />
                  <button
                    type="button"
                    onClick={applyCoupon}
                    disabled={couponLoading}
                    className={`px-6 h-9 text-14 font-normal ${couponCode.trim() ? 'bg-black text-white' : 'bg-[#BDBDBD] text-white'}`}
                  >
                    {couponLoading ? t('checkout.applying') : t('checkout.apply')}
                  </button>
                </div>

                {couponError && (
                  <div className="text-red-600 text-sm mt-2">{couponError}</div>
                )}

                {appliedCoupon && (
                  <div className="inline-flex items-center mt-2 border border-black">
                    <span className="text-14 font-normal pt-[9px] pl-[8px] pb-[8px] pr-2">
                      {appliedCoupon.code}
                    </span>
                    <button
                      type="button"
                      onClick={removeCoupon}
                      className="text-black text-14 font-normal pr-[7px]"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clip-path="url(#clip0_8535_368)">
                          <path d="M14.1753 14.1745L1.82617 1.82544" stroke="black" stroke-miterlimit="10" />
                          <path d="M1.82617 14.1745L14.1753 1.82544" stroke="black" stroke-miterlimit="10" />
                        </g>
                        <defs>
                          <clipPath id="clip0_8535_368">
                            <rect width="16" height="16" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              {/* Order Summary */}
              <div className="space-y-[4px] border-t border-black pt-2 mt-2">
                <div className="flex justify-between text-sm">
                  <span className='text-14 font-light'>{t('checkout.subtotal')}</span>
                  <span className='text-14 font-normal'>{formatPrice(cart.subtotal)}</span>
                </div>

                <div className="flex justify-between text-sm">
                  <span className='text-14 font-light'>{t('checkout.shipping')}</span>
                  <span className='text-14 font-normal'>
                    {freeShippingThreshold > 0 && cart.subtotal >= freeShippingThreshold
                      ? t('checkout.free')
                      : formatPrice(shippingCost)}
                  </span>
                </div>

                {appliedCoupon && (
                  <div className="flex justify-between text-sm">
                    <span className='text-14 font-light'>{t('checkout.discount')}</span>
                    <span className='text-14 font-normal'>
                      -{appliedCoupon.type === 'percent'
                        ? formatPrice(((cart.subtotal + shippingCost) * appliedCoupon.amount / 100))
                        : formatPrice(appliedCoupon.amount)}
                    </span>
                  </div>
                )}

                <div className="flex justify-between text-base font-medium border-t border-black pt-2 mt-2">
                  <span className='text-14 font-light'>{t('checkout.total')}</span>
                  <span className='text-14 font-normal'>{formatPrice(calculateFinalTotal())}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="lg:hidden mt-[19px]">
          <button
            type="submit"
            form="checkout-form"
            disabled={loading}
            className="w-full h-9 bg-black text-white text-14 font-normal disabled:cursor-not-allowed"
          >
            {loading
              ? t('checkout.processing')
              : formData.paymentMethod === 'cpay_gateway'
                ? t('checkout.proceedToCPay')
                : t('checkout.placeOrder')
            }
          </button>
        </div>
        <div className="grid lg:grid-cols-[350px_350px] lg:gap-2 gap-2 lg:justify-center">

          {/* Submit Button - Desktop only */}
          <div className="hidden lg:block -translate-y-[21px]">
            <button
              type="submit"
              form="checkout-form"
              disabled={loading}
              className="w-full h-9 bg-black text-white text-14 font-normal disabled:cursor-not-allowed mt-2"
            >
              {loading
                ? t('checkout.processing')
                : formData.paymentMethod === 'cpay_gateway'
                  ? t('checkout.proceedToCPay')
                  : t('checkout.placeOrder')
              }
            </button>
          </div>
        </div>
      </div>

      {isRedirectingToCPay && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
          <div className="bg-white p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
            <p className="text-lg font-medium">{t('checkout.redirectingToCPay')}</p>
            <p className="text-sm text-black mt-2">{t('checkout.redirectMessage')}</p>          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutPage;